import type { WorldAtlasAssetProvider } from '../assets/assetProvider';
import type { World2DCamera } from '../camera';
import type { StaticObjectRenderData } from '../contracts';
import { getGrid } from '../../../grid';

export class StaticObjectLayer {
  private readonly animatedOverlay: HTMLDivElement;
  private readonly requestRender: () => void;
  private readonly svgImageCache = new Map<string, HTMLImageElement>();
  private readonly pendingSvgImages = new Map<string, Promise<HTMLImageElement>>();

  constructor(animatedOverlay: HTMLDivElement, requestRender: () => void) {
    this.animatedOverlay = animatedOverlay;
    this.requestRender = requestRender;
  }

  public draw(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    objects: StaticObjectRenderData,
    assets: WorldAtlasAssetProvider,
    gridType: string,
    overscanPx = 0,
    options?: {
      readonly clipRect?: { x: number; y: number; width: number; height: number } | null;
      readonly screenOffsetX?: number;
      readonly screenOffsetY?: number;
      readonly updateAnimatedOverlay?: boolean;
    },
  ): void {
    const clipRect = options?.clipRect ?? null;
    const screenOffsetX = options?.screenOffsetX ?? 0;
    const screenOffsetY = options?.screenOffsetY ?? 0;
    const updateAnimatedOverlay = options?.updateAnimatedOverlay ?? true;
    const grid = getGrid(gridType);
    const pixelsPerWorldUnit = camera.getPixelsPerWorldUnit();
    const viewport = camera.getViewportSize();
    const visibleAnimatedIds = new Set<string>();
    if (clipRect || screenOffsetX !== 0 || screenOffsetY !== 0) {
      ctx.save();
      if (clipRect) {
        ctx.beginPath();
        ctx.rect(clipRect.x, clipRect.y, clipRect.width, clipRect.height);
        ctx.clip();
      }
      if (screenOffsetX !== 0 || screenOffsetY !== 0) {
        ctx.translate(screenOffsetX, screenOffsetY);
      }
    }
    for (const item of objects.items) {
      if (!item.assetId) {
        continue;
      }
      const frame = assets.getObjectFrame(item.assetId);
      if (!frame) {
        continue;
      }
      const page = assets.getPage(frame.pageId);
      if (!page) {
        continue;
      }

      const anchor = camera.tileCenterToScreen(item.mapX, item.mapY, grid);
      const drawWidth = frame.drawWidth * item.sizeMultiplier * pixelsPerWorldUnit;
      const drawHeight = frame.drawHeight * item.sizeMultiplier * pixelsPerWorldUnit;
      const drawAnchorX = anchor.x + (item.offsetX * pixelsPerWorldUnit);
      const drawAnchorY = anchor.y + ((item.offsetY + 0.5) * pixelsPerWorldUnit);
      const left = drawAnchorX - (drawWidth * frame.pivot.x);
      const top = drawAnchorY - (drawHeight * frame.pivot.y);
      if (
        left + drawWidth < -overscanPx
        || top + drawHeight < -overscanPx
        || left > viewport.width + overscanPx
        || top > viewport.height + overscanPx
      ) {
        continue;
      }

      if (frame.animated && frame.sourcePath) {
        if (!updateAnimatedOverlay) {
          continue;
        }
        const element = this.getOrCreateAnimatedOverlayElement(item.id, frame.sourcePath);
        element.style.display = 'block';
        element.style.width = `${Math.round(drawWidth)}px`;
        element.style.height = `${Math.round(drawHeight)}px`;
        element.style.transformOrigin = `${frame.pivot.x * 100}% ${frame.pivot.y * 100}%`;
        const transforms: string[] = [`translate3d(${left + overscanPx}px, ${top + overscanPx}px, 0)`];
        const scaleX = item.flipHorizontal ? -1 : 1;
        const scaleY = item.flipVertical ? -1 : 1;
        if (scaleX !== 1 || scaleY !== 1) {
          transforms.push(`scale(${scaleX}, ${scaleY})`);
        }
        if ((item.rotationSteps ?? 0) !== 0) {
          transforms.push(`rotate(${item.rotationSteps * 90}deg)`);
        }
        element.style.transform = transforms.join(' ');
        visibleAnimatedIds.add(item.id);
        continue;
      }

      const svgImage = frame.sourcePath && isSvgSourcePath(frame.sourcePath)
        ? this.getSvgImage(frame.sourcePath)
        : null;
      if (frame.sourcePath && isSvgSourcePath(frame.sourcePath) && !svgImage) {
        continue;
      }
      ctx.save();
      ctx.translate(drawAnchorX, drawAnchorY);
      ctx.scale(item.flipHorizontal ? -1 : 1, item.flipVertical ? -1 : 1);
      ctx.rotate((item.rotationSteps * Math.PI) / 2);
      if (svgImage) {
        ctx.drawImage(
          svgImage,
          -drawWidth * frame.pivot.x,
          -drawHeight * frame.pivot.y,
          drawWidth,
          drawHeight,
        );
      } else {
        ctx.drawImage(
          page.image,
          frame.pixelRect.x,
          frame.pixelRect.y,
          frame.pixelRect.width,
          frame.pixelRect.height,
          -drawWidth * frame.pivot.x,
          -drawHeight * frame.pivot.y,
          drawWidth,
          drawHeight,
        );
      }
      ctx.restore();
    }

    if (clipRect || screenOffsetX !== 0 || screenOffsetY !== 0) {
      ctx.restore();
    }

    if (updateAnimatedOverlay) {
      const children = this.animatedOverlay.children;
      for (let index = 0; index < children.length; index += 1) {
        const child = children[index];
        if (!(child instanceof HTMLImageElement)) {
          continue;
        }
        if (!visibleAnimatedIds.has(child.dataset.objectId ?? '')) {
          child.style.display = 'none';
        }
      }
    }
  }

  private getOrCreateAnimatedOverlayElement(objectId: string, sourcePath: string): HTMLImageElement {
    const existing = this.animatedOverlay.querySelector(`img[data-object-id="${CSS.escape(objectId)}"]`);
    if (existing instanceof HTMLImageElement) {
      if (existing.src !== new URL(sourcePath, window.location.href).href) {
        existing.src = sourcePath;
      }
      return existing;
    }

    const image = document.createElement('img');
    image.dataset.objectId = objectId;
    image.draggable = false;
    image.decoding = 'async';
    image.src = sourcePath;
    image.style.position = 'absolute';
    image.style.left = '0';
    image.style.top = '0';
    image.style.pointerEvents = 'none';
    image.style.display = 'none';
    image.style.transformOrigin = 'center';
    this.animatedOverlay.appendChild(image);
    return image;
  }

  private getSvgImage(sourcePath: string): HTMLImageElement | null {
    const existing = this.svgImageCache.get(sourcePath);
    if (existing) {
      return existing;
    }
    if (this.pendingSvgImages.has(sourcePath)) {
      return null;
    }

    const pending = loadSvgImage(sourcePath)
      .then((image) => {
        this.svgImageCache.set(sourcePath, image);
        this.requestRender();
        return image;
      })
      .finally(() => {
        this.pendingSvgImages.delete(sourcePath);
      });
    this.pendingSvgImages.set(sourcePath, pending);
    return null;
  }

}

function isSvgSourcePath(sourcePath: string): boolean {
  return /\.svg(?:\?|#|$)/i.test(sourcePath);
}

function loadSvgImage(sourcePath: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = document.createElement('img');
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load SVG object source '${sourcePath}'.`));
    image.src = sourcePath;
  });
}
